import React from 'react';
import { ErrorBoundary } from '../ErrorBoundary';
import { Button } from '../../ui/Button';
import Link from 'next/link';

interface PublicErrorFallbackProps {
  error?: Error;
  errorInfo?: React.ErrorInfo;
  retry?: () => void;
}

/**
 * Public-facing error fallback UI
 */
const PublicErrorFallback: React.FC<PublicErrorFallbackProps> = ({ error, retry }) => {
  const handleGoHome = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  const handleBrowseEvents = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/events';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg rounded-lg sm:px-10">
          <div className="text-center">
            {/* Public Error Icon */}
            <div className="mx-auto h-16 w-16 text-blue-600 mb-6">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Something went wrong
            </h2>

            <p className="text-gray-600 mb-6 text-sm">
              We&apos;re sorry for the inconvenience. Our team has been notified
              and we&apos;re working to fix this issue as quickly as possible.
            </p>

            {/* Action Buttons */}
            <div className="space-y-3 mb-6">
              {retry && (
                <Button
                  onClick={retry}
                  variant="solid"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Try Again
                </Button>
              )}

              <Button
                onClick={handleBrowseEvents}
                variant="secondary"
                className="w-full"
              >
                Browse Events
              </Button>

              <Button
                onClick={handleGoHome}
                variant="outline"
                className="w-full"
              >
                Go to Homepage
              </Button>
            </div>

            {/* Popular Links */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Popular pages:</h3>
              <div className="space-y-2 text-sm">
                <Link
                  href="/events"
                  className="block text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Upcoming Events
                </Link>
                <Link
                  href="/information"
                  className="block text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Venue Information
                </Link>
                <Link
                  href="/signup"
                  className="block text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Create Account
                </Link>
              </div>
            </div>

            {/* Error Details for Development */}
            {process.env.NODE_ENV === 'development' && error && (
              <details className="mt-6 text-left bg-gray-50 p-4 rounded text-xs">
                <summary className="cursor-pointer font-medium text-gray-700">
                  Error Details (Development Only)
                </summary>
                <pre className="mt-2 whitespace-pre-wrap text-xs text-gray-600">
                  {error.message}
                  {error.stack}
                </pre>
              </details>
            )}

            {/* Status Information */}
            <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500">
              <p className="mb-1">
                <strong>Status:</strong> Investigating
              </p>
              <p className="mb-2">
                <strong>Incident ID:</strong> {Date.now().toString(36)}
              </p>

              <details className="mt-2">
                <summary className="cursor-pointer hover:text-gray-700">
                  Technical Details
                </summary>
                <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono text-left">
                  <p><strong>User Agent:</strong> {typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown'}</p>
                  <p><strong>URL:</strong> {typeof window !== 'undefined' ? window.location.href : 'Unknown'}</p>
                  <p><strong>Timestamp:</strong> {new Date().toISOString()}</p>
                </div>
              </details>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Public Error Boundary Component
 * Specialized for public-facing pages and user experience
 */
export const PublicErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ErrorBoundary
      fallback={PublicErrorFallback}
      onError={(error, errorInfo) => {
        // Log public-facing errors
        console.error('[PublicErrorBoundary] Public page error:', error);
        console.error('[PublicErrorBoundary] Error info:', errorInfo);

        // Send to monitoring service with public context
        if (typeof window !== 'undefined' && (window as any).gtag) {
          (window as any).gtag('event', 'exception', {
            description: `Public Error: ${error.message}`,
            fatal: false,
            custom_map: { public_error: true }
          });
        }
      }}
      errorTitle="Page Error"
      errorMessage="We're experiencing technical difficulties. Please try again or browse our events."
      maxRetries={3}
      showErrorDetails={process.env.NODE_ENV === 'development'}
    >
      {children}
    </ErrorBoundary>
  );
};
